import React, { useCallback, useState } from 'react';
import {
  oneOfType, number, object, bool, string
} from 'prop-types';
import _reduce from 'lodash/reduce';
import _groupBy from 'lodash/groupBy';
import _indexOf from 'lodash/indexOf';
import classNames from 'classnames';
import {
  Button, Stepper
} from '@one-thd/sui-atomic-components';
import { useDigitalDataStore, useProductDataStore } from '../../../store';
import { useDesignStore } from '../../../store/design/context';
import logNewRelicCustom from '../../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS, CONFIGURATOR } from '../../../constants';

const quantityReducer = (group) => _reduce(group, (prev, curr) => {
  let count = prev || 0;
  if (curr?.Quantity) {
    count += curr.Quantity;
    return count;
  }
  count += 1;
  return count;
}, 0);

const QuantityStepper = ({
  sku, id, choiceMetaData, isAvailable, disabled, responsiveTilePaddingStyle
}) => {
  const [delayed, setDelayed] = useState(false);

  const delayedUpdateQty = useCallback((func, qty = null) => {
    setDelayed(true);
    if (qty) {
      func(sku, qty);
      setTimeout(() => setDelayed(false), 500);
      return () => false;
    }

    func(sku);
    setTimeout(() => setDelayed(false), 500);
    return () => false;
  }, []);
  const productData = useProductDataStore((state) => state.productData);
  const graphProduct = productData?.[sku];
  const trackAddItemToStackEvent = useDigitalDataStore((state) => state.trackAddItemToStackEvent);
  const addNonVisualizedProduct = useDesignStore((state) => state.addNonVisualizedProduct);
  const addAccessory = useDesignStore((state) => state.addAccessory);
  const handleAddAccessoryClick = useCallback(() => {
    addAccessory(true);
    addNonVisualizedProduct(sku);
    trackAddItemToStackEvent(sku, graphProduct?.pricing?.value);
    logNewRelicCustom(NEW_RELIC_EVENTS.ADD_ACCESSORY_PRODUCT, { choiceId: id, sku });
  }, [graphProduct]);

  const products = useDesignStore((state) => state.design.products);
  const cart = useDesignStore((state) => state.design.cart);
  const groupedProductsBySku = _groupBy([...cart, ...products], 'Sku');
  const quantity = quantityReducer(groupedProductsBySku?.[sku]);
  const removeNonVisualizedProduct = useDesignStore(
    (state) => state.removeNonVisualizedProduct
  );
  const updateNonVisualizedQty = useDesignStore(
    (state) => state.updateNonVisualizedQty
  );
  const isAccessory = choiceMetaData?.ProductType === CONFIGURATOR.CHOICE.ACCESSORY && isAvailable;
  const isDisabled = () => {
    if (isAccessory) {
      return false;
    }
    return disabled ? _indexOf(disabled, id) !== -1 : false;
  };
  const allDisabled = isDisabled() || !isAvailable;

  return quantity
    ? (
      <div style={responsiveTilePaddingStyle}>
        <Stepper
          readOnly
          value={quantity}
          disabled={delayed}
          min={0}
          max={30}
          onChange={(val) => {
            if (val === 0) {
              delayedUpdateQty(removeNonVisualizedProduct, val);
            } else {
              delayedUpdateQty(updateNonVisualizedQty, val);
            }
            trackAddItemToStackEvent(sku, graphProduct?.pricing?.value);
            return logNewRelicCustom(NEW_RELIC_EVENTS.UPDATE_SUMMARY_QTY, {
              itemId: sku,
              quantity: val,
            });
          }}
        />
      </div>
    )
    : (
      <div className={classNames({
        'button__accessory-add--disabled': allDisabled,
        'icon__accessory-add--disabled': allDisabled,
        'button__accessory-add': !allDisabled,
        'icon__accessory-add': !allDisabled
      })}
      >
        <Button
          style={{ padding: 0 }}
          fullWidth
          variant="secondary"
          disabled={allDisabled ? disabled : false}
          onClick={allDisabled ? null : () => handleAddAccessoryClick()}
        >
          Add To Bundle
        </Button>
      </div>

    );
};

export default QuantityStepper;

QuantityStepper.propTypes = {
  id: oneOfType([string, number]).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  choiceMetaData: object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  responsiveTilePaddingStyle: object.isRequired,
  isAvailable: bool.isRequired,
  disabled: bool.isRequired,
  sku: oneOfType([string, number]).isRequired
};
